/**
 * `parseFormattedNumber` — safe parser for PRE-FORMATTED display
 * strings ("5.222", "66.9%", "$1,234.5", "15,7 wk.") so the KPI
 * entrance can count from 0 to the value IN THE ORIGINAL FORMAT
 * (motion ronda 14, decision B).
 *
 * BI hard rule (non-negotiable): the FINAL frame renders the original
 * string byte-identical — `render(value)` returns the exact input
 * string literally, never a re-derivation. Only INTERMEDIATE frames
 * are generated, using the prefix/suffix/separators detected in the
 * original.
 *
 * The decimal-separator heuristic is deliberately CONSERVATIVE — any
 * ambiguity returns `null` (the caller falls back to a fade, never to
 * a guessed number):
 *   - both "." and "," present → the LAST one is the decimal
 *     separator; the other must group digits in threes.
 *   - a single separator, appearing once, with 1–2 digits after it →
 *     decimal ("15,7", "66.9").
 *   - a single separator type grouping digits in exact threes (first
 *     group 1–3) → thousands ("5.222", "1 234 567").
 *   - a single separator, once, with 4+ digits after it → decimal
 *     (no thousands group has 4 digits: "3.1416").
 *   - anything else (a lone separator with 3 digits after it that
 *     doesn't group cleanly, mixed group widths, adjacent separators,
 *     a sign inside the suffix, no digits at all) → null.
 *
 * Space separators (incl. NBSP/narrow NBSP) are always thousands.
 */

export interface ParsedFormattedNumber {
  /** The numeric value the string represents. */
  value: number;
  /**
   * Formats an intermediate frame in the ORIGINAL format (same
   * prefix, suffix, separators, decimal count). For `n === value`
   * returns the original input string literally (byte-identical
   * guarantee).
   */
  render: (n: number) => string;
}

/** Thousands-group chars: dot, comma handled separately; spaces here. */
const SPACE_SEPS = new Set([" ", " ", " "]);

function isSep(ch: string): boolean {
  return ch === "." || ch === "," || SPACE_SEPS.has(ch);
}

function groupThousands(intDigits: string, sep: string): string {
  let out = "";
  for (let i = 0; i < intDigits.length; i++) {
    const fromEnd = intDigits.length - i;
    if (i > 0 && fromEnd % 3 === 0) out += sep;
    out += intDigits[i]!;
  }
  return out;
}

export function parseFormattedNumber(s: string): ParsedFormattedNumber | null {
  if (typeof s !== "string" || s.length === 0) return null;
  // prefix (no digits, no sign) · optional sign · numeric core
  // (starts and ends with a digit) · suffix (no digits, no sign).
  const m = /^([^0-9\-−]*)([-−]?)(\d(?:[\d.,   ]*\d)?)([^0-9\-−]*)$/.exec(
    s,
  );
  if (!m) return null;
  // Regex groups are always present on a match (possibly empty) —
  // the `!` are type narrows for noUncheckedIndexedAccess, not
  // data fallbacks.
  const prefix = m[1]!;
  const sign = m[2]!;
  const core = m[3]!;
  const suffix = m[4]!;
  // A prefix ending (or suffix starting) with a separator char makes
  // the number's boundary ambiguous (".5", "1.") → null.
  if (/[.,]$/.test(prefix) || /^[.,]/.test(suffix)) return null;

  // Tokenize the core into digit runs and single separator chars.
  const groups: string[] = [];
  const seps: string[] = [];
  let cur = "";
  for (const ch of core) {
    if (isSep(ch)) {
      if (cur === "") return null; // adjacent separators / leading sep
      groups.push(cur);
      cur = "";
      seps.push(ch);
    } else {
      cur += ch;
    }
  }
  groups.push(cur);

  let intDigits: string;
  let fracDigits = "";
  let decimalSep = "";
  let thousandsSep = "";

  const groupedInThrees = (gs: string[]): boolean =>
    gs.length > 0 &&
    gs[0]!.length >= 1 &&
    gs[0]!.length <= 3 &&
    gs.slice(1).every((g) => g.length === 3);

  if (seps.length === 0) {
    intDigits = groups[0]!;
  } else {
    // Normalize space variants to one thousands char (keep the FIRST
    // space char seen so render reproduces the exact byte).
    const kinds = new Set(seps.map((c) => (SPACE_SEPS.has(c) ? "space" : c)));
    const last = seps[seps.length - 1]!;
    const lastKind = SPACE_SEPS.has(last) ? "space" : last;
    if (kinds.size > 2) return null;
    if (kinds.size === 2) {
      // Mixed separators: the LAST one is the decimal — it must appear
      // exactly once, spaces are never decimal, and the rest must
      // group in threes.
      if (lastKind === "space") return null;
      if (seps.filter((c) => c === last).length !== 1) return null;
      const thouSeps = seps.slice(0, -1);
      const thouKind = SPACE_SEPS.has(thouSeps[0]!) ? "space" : thouSeps[0]!;
      if (thouSeps.some((c) => (SPACE_SEPS.has(c) ? "space" : c) !== thouKind)) return null;
      const intGroups = groups.slice(0, -1);
      if (!groupedInThrees(intGroups)) return null;
      decimalSep = last;
      thousandsSep = thouSeps[0]!;
      intDigits = intGroups.join("");
      fracDigits = groups[groups.length - 1]!;
    } else if (lastKind === "space") {
      // Spaces are always thousands.
      if (!groupedInThrees(groups)) return null;
      thousandsSep = seps[0]!;
      intDigits = groups.join("");
    } else if (seps.length === 1) {
      const after = groups[1]!;
      if (after.length <= 2) {
        // 1–2 digits after a lone . or , → decimal ("66.9", "15,7").
        decimalSep = seps[0]!;
        intDigits = groups[0]!;
        fracDigits = after;
      } else if (after.length === 3 && groupedInThrees(groups)) {
        // Exact 3-grouping → thousands ("5.222").
        thousandsSep = seps[0]!;
        intDigits = groups.join("");
      } else if (after.length >= 4 && groups[0]!.length >= 1) {
        // 4+ decimals can't be a thousands group ("3.1416").
        decimalSep = seps[0]!;
        intDigits = groups[0]!;
        fracDigits = after;
      } else {
        return null; // e.g. "1234.567" — neither clean grouping nor 1–2 decimals
      }
    } else {
      // Same separator repeated → thousands only, exact threes.
      if (!groupedInThrees(groups)) return null;
      thousandsSep = seps[0]!;
      intDigits = groups.join("");
    }
  }

  const decimals = fracDigits.length;
  const magnitude = Number(`${intDigits}${decimals > 0 ? `.${fracDigits}` : ""}`);
  if (!Number.isFinite(magnitude)) return null;
  const value = sign ? -magnitude : magnitude;

  const render = (n: number): string => {
    // Byte-identical guarantee: the target value renders the ORIGINAL
    // string, never a reconstruction.
    if (Object.is(n, value) || n === value) return s;
    const neg = n < 0;
    const fixed = Math.abs(n).toFixed(decimals);
    const [ip = "", fp = ""] = fixed.split(".");
    const grouped = thousandsSep ? groupThousands(ip, thousandsSep) : ip;
    const signChar = neg ? (sign || "-") : "";
    return `${prefix}${signChar}${grouped}${decimals > 0 ? `${decimalSep}${fp}` : ""}${suffix}`;
  };

  return { value, render };
}
